
import { Alert, Button, Chip, Divider, FormHelperText, Link, Switch, Tooltip, TooltipProps, styled, tooltipClasses} from '@mui/material'
import {
  BorderedBox,
  CCTFormControlLabel,
  ContentBox,
  GridLabel,
  GridValue,
  Heading2,
  PackageFeatureContainer,
} from './ClientConfigToolStyles'
import { CCTConfig } from '../atoms/wotcConfigState'
import { ChangeEvent } from 'react'
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline'
import ConfigureWotcEditHistory from './ConfigureWotcEditHistory'
import { FormControl } from '@mui/material'
import { Grid } from '@mui/material'
import { InputLabel } from '@mui/material'
import { MenuItem } from '@mui/material'
import { Provider } from '../atoms/wotcApplicantState'
import React from 'react'
import { Select } from '@mui/material'
import { SelectChangeEvent } from '@mui/material'
import { TextField } from '@mui/material'
import UnlinkCredentialsConfirmDialog from './UnlinkCredentialsConfirmDialog'
import WotcApplicantService from '../service/wotcapplicant'
import { WotcConfig } from '../atoms/wotcConfigState'
import { useEffect } from 'react'
import { useRecoilValue } from 'recoil'
import { useSetRecoilState } from 'recoil'
import { useState } from 'react'

import wotcConfigState from '../atoms/wotcConfigState'

type WotcSettingsProps = {
  wotcConfig: WotcConfig,
  cctConfig: CCTConfig,
  linkCredentialsEnabled: boolean,
  setLinkCredentialsEnabled: (value: boolean) => void,
  sourceTrUuid: string,
  setSourceTrUuid: (value: string) => void,
}

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    height: 40,
    maxWidth: 160
  },
})

function ConfigureWotcSettings({
  wotcConfig,
  cctConfig,
  linkCredentialsEnabled,
  setLinkCredentialsEnabled,
  sourceTrUuid,
  setSourceTrUuid,
} : WotcSettingsProps) {
  const [allProviders, setAllProviders] = useState<Provider[]>([])
  const [showUnlinkDialog, setShowUnlinkDialog] = useState(false)
  const [unlinking, setUnlinking] = useState(false)

  const updateWotcConfigState = useSetRecoilState(wotcConfigState)
  const wotcState = useRecoilValue(wotcConfigState)

  const isLinked = Boolean(wotcConfig.linkedFromClientId)

  const handleUnlink = async () => {
    setUnlinking(true)
    try {
      await WotcApplicantService.unlinkCredentials(wotcConfig.clientId)
      updateWotcConfigState({
        ...wotcState,
        cctConfig: {
          ...cctConfig,
          alertMessage: 'Credentials unlinked successfully',
          alertSeverity: 'success',
          alertTitle: 'Success',
          showAlert: true,
        },
        wotcConfig: {
          ...wotcConfig,
          linkedFromClientId: undefined,
        },
      })
      setLinkCredentialsEnabled(false)
      setSourceTrUuid('')
    } catch (e) {
      const err = e as {
        response?: {
          data?: { developerMessage?: string, message?: string, detail?: string, title?: string }
        }
      }
      const data = err.response?.data
      const title = data?.message || data?.title || 'Unknown error'
      const message = data?.developerMessage || data?.detail || 'Failed to unlink credentials.'
      updateWotcConfigState({
        ...wotcState,
        cctConfig: {
          ...cctConfig,
          alertMessage: message,
          alertSeverity: 'error',
          alertTitle: `Error - ${title}`,
          showAlert: true,
        },
      })
    } finally {
      setUnlinking(false)
      setShowUnlinkDialog(false)
    }
  }
  
  useEffect(() => {
    async function fetchProviders() {
      try {
        const result = await WotcApplicantService.getAllProviders()
        setAllProviders(result.providers)
      } catch (error) {
        console.error('Error fetching providers:', error)
      }
    }
    fetchProviders()
  }, [])

  const handleSelect = (event: SelectChangeEvent) => {
    updateWotcConfigState({
      ...wotcState,
      cctConfig: {
        ...cctConfig,
        vendorListError: !event.target.value
      },
      wotcConfig: {
        ...wotcConfig,
        selectedOrganizationCode: wotcConfig.providerName === event.target.value && wotcConfig.organizationCode ? wotcConfig.organizationCode : '',
        [event.target.name]: event.target.value
      },
    })    
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {    
    updateWotcConfigState({
      ...wotcState,
      cctConfig: {
        ...cctConfig,
        vendorListError: false
      },
      wotcConfig: {
        ...wotcConfig,
        selectedOrganizationCode: '',
        selectedProvider: '',
        [event.target.name]: event.target.checked,
      },
    })
  }

  const getAdpConsentUrl =  (client_id: string,) => {
    const consentUrl = `${process.env.REACT_APP_ADP_CONSENT_URL}=${process.env.REACT_APP_ADP_CONSUMER_APPLICATION_ID}&successUri=${process.env.REACT_APP_WOTC_APPLICANT_SERVICE_EXTERNAL}/v2/providers/adp/${client_id}/dataConsentCallback`
    return consentUrl 
  }

  useEffect(() => {
    if (!wotcConfig.selectedProvider && wotcConfig.providerName) {
      updateWotcConfigState({
        ...wotcState,
        wotcConfig: {
          ...wotcConfig,
          selectedProvider: wotcConfig.providerName,
        },
      })
    }
  }, [wotcConfig.providerName, wotcConfig.selectedProvider])

  return (
    <BorderedBox>
      <ContentBox>
        <Grid container direction="column" rowSpacing={2.35}>
          <Grid container item justifyContent="space-between">
            <Grid item>         
              <Heading2>WOTC configuration</Heading2>
            </Grid>
            <Grid item>
              <ConfigureWotcEditHistory clientId={wotcConfig.clientId} />
            </Grid>
          </Grid>
          <Grid container direction="column" item rowSpacing={2.35}>
         
            <Grid item md={10}>
              <CCTFormControlLabel control={<Switch checked={wotcConfig.active || false} name="active" onChange={handleChange} />} label="Enable WOTC2" />
            </Grid>

            <Grid container direction="column" item rowSpacing={1}>
              <Grid alignItems="center" columns={16} container direction="row" item>
                <GridLabel item xs={2.2}>
                  Current Vendor
                </GridLabel>
                <GridValue item>{wotcConfig.providerName}</GridValue>
              </Grid>
              <Grid alignItems="center" columns={16} container direction="row" item>
                <GridLabel item xs={2.2}>
                  Client UUID
                </GridLabel>
                <GridValue item>{wotcConfig.clientId}</GridValue>
              </Grid>
              <Grid alignItems="center" columns={16} container direction="row" item>
                <GridLabel item xs={2.2}>
                  Current Vendor Organization Code
                </GridLabel>
                <GridValue item>{wotcConfig.organizationCode}</GridValue>
              </Grid>
              {
                (wotcConfig.providerName === 'ADP' && wotcConfig.clientId) ? 
                  <Grid alignItems="center" columns={16} container direction="row" item>
                    <GridLabel item xs={2.2}>
                      Consent Link
                    </GridLabel>
                    <GridValue item xs={13.8}>
                      <Link href={getAdpConsentUrl(wotcConfig.clientId)}>
                        {
                          getAdpConsentUrl(wotcConfig.clientId)
                        }
                      </Link>
                    
                    </GridValue>
                  </Grid> : ''
              }
            </Grid>

            <PackageFeatureContainer/>
            <Grid alignItems="center" columns={16} container direction="row" item>
              <FormControl component="fieldset" error={wotcState.cctConfig.vendorListError}>
                <InputLabel id="selectLabelId">Vendor List *</InputLabel>
                <Select disabled={!wotcConfig.active}                
                  id="select-providers"
                  label="Vendor List"
                  labelId="selectLabelId"
                  name="selectedProvider"
                  onChange={handleSelect} sx={{ width: 300 }}
                  value={wotcConfig.selectedProvider || wotcConfig.providerName}>
                  {allProviders?.map((provider) => (
                    <MenuItem key={provider.name} value={provider.name}>
                      {provider.companyName}
                    </MenuItem>
                  ))}
                </Select>
                {wotcState.cctConfig.vendorListError && <FormHelperText >Field is mandatory *</FormHelperText>}
              </FormControl>
            </Grid>
          
            {(wotcConfig.selectedProvider === 'EH' || wotcConfig.selectedProvider === 'SYNERGI') && (wotcConfig.active) &&
            <Grid alignItems="center" container direction="row" item>
              <GridValue item>
                <TextField
                  error={wotcState.cctConfig.orgError}
                  fullWidth
                  helperText={wotcState.cctConfig.orgError ? 'Field is mandatory' : ''}
                  id="selectedOrganizationCode"
                  inputProps={{ maxLength : 36 }}
                  label="Organization Code *"
                  name="selectedOrganizationCode"
                  onChange={event => {
                    updateWotcConfigState({
                      ...wotcState,
                      cctConfig: {
                        ...cctConfig,
                        orgError: !event.target.value
                      },
                      wotcConfig: {
                        ...wotcConfig,
                        selectedOrganizationCode: event.target.value
                      },
                    })
                  }}
                  sx={{ width: 300 }}
                  value={wotcConfig.selectedOrganizationCode}
                  variant="outlined" />
              </GridValue>
            </Grid>
            }

            {(wotcConfig.selectedProvider === 'ADP' || (wotcConfig.providerName === 'ADP' && wotcConfig.selectedProvider === '')) &&
                <>
                  <Grid alignItems="center" columns={16} container direction="row" item >
                    <Grid item md={10} rowSpacing={2}>
                      {(wotcConfig.marketIntegration) &&
                        <>
                          <Chip icon={<CheckCircleOutline color='success' sx={{height: '16px', width: '16px'}} />}  label="ADP Marketplace Integration Completed"  sx={{ background: 'linear-gradient(0deg, rgba(255, 255, 255, 0.87), rgba(255, 255, 255, 0.87)), #127E09', color: 'darkgreen', fontWeight: '500', height: '28px'}} />
                        </>
                      }
                      {(!wotcConfig.marketIntegration) &&
                        <>
                          <CustomWidthTooltip arrow placement="bottom-start" title="Set up the WOTC assessment on ADP MarketPlace website" >
                            <Chip label="ADP Marketplace Integration Incomplete"  sx={{ bgcolor: '#D4D4D4', color: '000000', height: '28px' }} />
                          </CustomWidthTooltip>
                        </>
                      }

                    </Grid>
                  </Grid>

                  <Grid alignItems="center" columns={16}  container direction="row" item >
                    <GridValue item><Alert severity="info">Please provide the client with the Client UUID code and Consent Link to complete WOTC enablement in the ADP Marketplace App. Once these steps have been completed in ADP, please refresh the screen to verify setup.</Alert>
                    </GridValue>
                  </Grid>
                </>
            }

            {wotcConfig.active && (wotcConfig.selectedProvider === 'ADP' || (wotcConfig.providerName === 'ADP' && wotcConfig.selectedProvider === '')) &&
              <>
                <Grid item xs={16}>
                  <Divider>OR</Divider>
                </Grid>
                <Grid item md={10}>
                  <CCTFormControlLabel
                    control={
                      <Switch
                        checked={linkCredentialsEnabled}
                        disabled={isLinked}
                        name="linkCredentialsEnabled"
                        onChange={event => {
                          setLinkCredentialsEnabled(event.target.checked)
                          if (!event.target.checked) {
                            setSourceTrUuid('')
                          }
                        }} />
                    }
                    label="Link credentials from another TR client" />
                </Grid>
                {linkCredentialsEnabled &&
                  <Grid alignItems="center" columnSpacing={2} container direction="row" item>
                    <GridValue item>
                      <TextField
                        disabled={isLinked}
                        fullWidth
                        id="sourceTrUuid"
                        inputProps={{ maxLength: 36 }}
                        label="Source Client UUID"
                        name="sourceTrUuid"
                        onChange={event => setSourceTrUuid(event.target.value)}
                        sx={{ width: 300 }}
                        value={sourceTrUuid}
                        variant="outlined" />
                    </GridValue>
                    {isLinked &&
                      <Grid item>
                        <Button
                          color="error"
                          disabled={unlinking}
                          onClick={() => setShowUnlinkDialog(true)}
                          variant="outlined">
                          Unlink
                        </Button>
                      </Grid>
                    }
                  </Grid>
                }
                {showUnlinkDialog &&
                  <UnlinkCredentialsConfirmDialog
                    handleClose={() => setShowUnlinkDialog(false)}
                    handleSubmit={() => { handleUnlink() }}
                    open={showUnlinkDialog} />
                }
              </>
            }

          </Grid>
        </Grid>
      </ContentBox>
    </BorderedBox>
  )
}

export default ConfigureWotcSettings